import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Image,
  StyleSheet,
} from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';

import BasicLayout from '../../common/components/CustomLayout/BasicLayout';
import { useAlertModal } from '../../common/hooks';
import {
  getConversations,
  getConversationThread,
  postSupportReply,
  markConversationRead,
  getCustomerContext,
} from '../../api/Support';

const PAGE_SIZE = 25;

type Conversation = {
  phone: string;
  customerName: string | null;
  customerEmail: string | null;
  orderNumber: string | null;
  lastBody: string;
  lastSender: string | null;
  lastAt: string;
  unread: number;
};

type Msg = {
  id: number;
  sender: 'customer' | 'admin';
  body: string;
  adminName: string | null;
  orderNumber: string | null;
  createdAt: string;
};

const fmt = (v: string) => {
  const d = new Date(v);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true });
};

const Messages = ({ navigation }) => {
  const { showAlert } = useAlertModal();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState<Conversation | null>(null);
  const [thread, setThread] = useState<Msg[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [context, setContext] = useState<{ orders: any[]; reports: any[] }>({ orders: [], reports: [] });
  const [contextLoading, setContextLoading] = useState(false);
  const [detail, setDetail] = useState<{ type: 'order' | 'report'; data: any } | null>(null);

  const loadConversations = (silent = false) => {
    if (!silent) setLoading(true);
    getConversations({ page, limit: PAGE_SIZE, search: search.trim() }, (jsonRes, status) => {
      if (!silent) setLoading(false);
      if (status === 200) {
        setConversations(jsonRes?.conversations || []);
        if (jsonRes?.pagination) {
          setTotal(jsonRes.pagination.total ?? 0);
          setTotalPages(jsonRes.pagination.total_pages ?? 1);
        }
      } else if (!silent) {
        showAlert('error', 'Could not load conversations.');
      }
    });
  };

  // Load on page/search change (debounced).
  useEffect(() => {
    const t = setTimeout(() => loadConversations(false), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  // Poll the conversation list every 15s (silent — no spinner) so new messages
  // surface without a manual refresh. Bound to page/search so it stays current.
  useEffect(() => {
    const iv = setInterval(() => loadConversations(true), 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  // Poll the open conversation every 10s (silent) so replies appear live.
  useEffect(() => {
    if (!selected) return;
    const iv = setInterval(() => refreshThread(selected.phone), 10000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const openThread = (c: Conversation) => {
    setSelected(c);
    setThread([]);
    setThreadLoading(true);
    getConversationThread(c.phone, (jsonRes, status) => {
      setThreadLoading(false);
      if (status === 200) setThread(jsonRes?.messages || []);
      else showAlert('error', 'Could not load the conversation.');
    });
    markConversationRead(c.phone, () => { /* clear unread badge locally */
      setConversations((prev) => prev.map((x) => (x.phone === c.phone ? { ...x, unread: 0 } : x)));
    });
    setDetail(null);
    setContext({ orders: [], reports: [] });
    setContextLoading(true);
    getCustomerContext(c.phone, (jr, s) => {
      setContextLoading(false);
      if (s === 200) setContext({ orders: jr?.orders || [], reports: jr?.reports || [] });
    });
  };

  const refreshThread = (phone: string) => {
    getConversationThread(phone, (jr, s) => { if (s === 200) setThread(jr?.messages || []); });
  };

  const sendReply = () => {
    const body = reply.trim();
    if (!body || !selected || sending) return;
    setSending(true);
    postSupportReply(selected.phone, body, (jsonRes, status) => {
      setSending(false);
      if (status === 200) { setReply(''); refreshThread(selected.phone); }
      else showAlert('error', 'Could not send the reply.');
    });
  };

  const displayName = (c: Conversation | null) => (c?.customerName || c?.phone || '');

  // Open an order's detail from a chat tag/header. Uses the loaded context order
  // when available; otherwise shows a minimal detail with just the number.
  const openOrderDetail = (orderNumber?: string | null) => {
    if (!orderNumber) return;
    const found = context.orders.find((o: any) => o.orderNumber === orderNumber);
    setDetail({ type: 'order', data: found || { orderNumber } });
  };

  return (
    <BasicLayout navigation={navigation} screenName={'Messages'}>
      <View style={styles.wrap}>
        {/* LEFT: threads */}
        <View style={styles.left}>
          <View style={styles.leftHeader}>
            <Text style={styles.title}>Messages</Text>
            {loading && <ActivityIndicator size="small" color="#2e96e1" />}
          </View>
          <TextInput style={styles.search} value={search} onChangeText={(t) => { setSearch(t); setPage(1); }} placeholder="Search name, phone or text" />

          <ScrollView style={styles.threadsList}>
            {conversations.length === 0 && !loading ? (
              <Text style={styles.empty}>No conversations yet.</Text>
            ) : conversations.map((c) => {
              const active = selected?.phone === c.phone;
              return (
                <TouchableOpacity key={c.phone} style={[styles.convRow, active && styles.convRowActive]} onPress={() => openThread(c)}>
                  <View style={styles.avatar}><FontAwesome5 name="user" size={13} color="#fff" /></View>
                  <View style={{ flex: 1 }}>
                    <View style={styles.convTop}>
                      <Text style={styles.convName} numberOfLines={1}>{c.customerName || c.phone}</Text>
                      <Text style={styles.convTime}>{fmt(c.lastAt)}</Text>
                    </View>
                    <Text style={styles.convSub} numberOfLines={1}>{c.phone}{c.orderNumber ? `  ·  Order #${c.orderNumber}` : ''}</Text>
                    <Text style={styles.convPreview} numberOfLines={1}>{c.lastSender === 'admin' ? 'You: ' : ''}{c.lastBody}</Text>
                  </View>
                  {c.unread > 0 && <View style={styles.badge}><Text style={styles.badgeText}>{c.unread}</Text></View>}
                </TouchableOpacity>
              );
            })}
          </ScrollView>

          <View style={styles.pagination}>
            <Text style={styles.pageInfo}>{total}</Text>
            <TouchableOpacity style={[styles.pageBtn, page <= 1 && styles.pageBtnDisabled]} disabled={page <= 1 || loading} onPress={() => setPage((p) => Math.max(1, p - 1))}><Text style={styles.pageBtnText}>Prev</Text></TouchableOpacity>
            <Text style={styles.pageInfo}>{`${page}/${totalPages}`}</Text>
            <TouchableOpacity style={[styles.pageBtn, page >= totalPages && styles.pageBtnDisabled]} disabled={page >= totalPages || loading} onPress={() => setPage((p) => Math.min(totalPages, p + 1))}><Text style={styles.pageBtnText}>Next</Text></TouchableOpacity>
          </View>
        </View>

        {/* RIGHT: conversation */}
        <View style={styles.right}>
          {!selected ? (
            <View style={styles.noSel}><FontAwesome5 name="comments" size={26} color="#c2ccd6" /><Text style={styles.noSelText}>Select a conversation to view and reply.</Text></View>
          ) : (
            <>
              <View style={styles.threadHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.threadName}>{displayName(selected)}</Text>
                  <Text style={styles.threadSub}>
                    {selected.phone}{selected.customerEmail ? `  ·  ${selected.customerEmail}` : ''}
                  </Text>
                </View>
                {!!selected.orderNumber && (
                  <TouchableOpacity style={styles.orderChip} onPress={() => openOrderDetail(selected.orderNumber)}><FontAwesome5 name="box" size={11} color="#1F6FB2" /><Text style={styles.orderChipText}>Order #{selected.orderNumber}</Text></TouchableOpacity>
                )}
              </View>

              <ScrollView style={styles.threadScroll} contentContainerStyle={{ padding: 14, gap: 8 }}>
                {threadLoading ? <ActivityIndicator color="#2e96e1" /> : thread.map((m) => (
                  <View key={m.id} style={[styles.msgRow, m.sender === 'admin' ? styles.msgRight : styles.msgLeft]}>
                    <View style={[styles.msg, m.sender === 'admin' ? styles.msgAdmin : styles.msgCustomer]}>
                      {!!m.orderNumber && m.sender === 'customer' && <Text style={styles.msgOrder} onPress={() => openOrderDetail(m.orderNumber)}>Order #{m.orderNumber} ›</Text>}
                      <Text style={[styles.msgBody, m.sender === 'admin' && { color: '#fff' }]}>{m.body}</Text>
                      <Text style={[styles.msgTime, m.sender === 'admin' ? { color: 'rgba(255,255,255,0.8)' } : { color: '#888' }]}>{m.sender === 'admin' ? (m.adminName || 'You') + ' · ' : ''}{fmt(m.createdAt)}</Text>
                    </View>
                  </View>
                ))}
              </ScrollView>

              <View style={styles.composer}>
                <TextInput style={styles.composerInput} value={reply} onChangeText={setReply} placeholder="Type a reply…" multiline />
                <TouchableOpacity style={[styles.sendBtn, (!reply.trim() || sending) && styles.pageBtnDisabled]} disabled={!reply.trim() || sending} onPress={sendReply}>
                  <Text style={styles.sendBtnText}>{sending ? '…' : 'Send'}</Text>
                </TouchableOpacity>
              </View>
            </>
          )}
        </View>

        {/* RIGHT: customer info panel — lists on top, detail below */}
        {selected && (
          <View style={styles.info}>
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 14, gap: 8 }}>
              <Text style={styles.infoTitle}>Customer</Text>
              <View style={styles.infoCard}>
                <Text style={styles.infoName}>{selected.customerName || selected.phone}</Text>
                <Text style={styles.infoLine}>{selected.phone}</Text>
                {!!selected.customerEmail && <Text style={styles.infoLine}>{selected.customerEmail}</Text>}
              </View>

              <Text style={styles.infoTitle}>Orders</Text>
              {contextLoading ? <ActivityIndicator color="#2e96e1" />
                : context.orders.length === 0 ? <Text style={styles.infoEmpty}>No orders found.</Text>
                : context.orders.slice(0, 12).map((o: any) => {
                  const active = detail?.type === 'order' && detail.data === o;
                  return (
                    <TouchableOpacity key={`${o.id}-${o.tenant || ''}`} style={[styles.infoRow, active && styles.infoRowActive]} onPress={() => setDetail({ type: 'order', data: o })}>
                      <FontAwesome5 name="box" size={11} color="#1F6FB2" />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.infoRowTitle}>#{o.orderNumber}</Text>
                        <Text style={styles.infoRowSub} numberOfLines={1}>{[o.storeName || o.tenantDisplayName, o.startDate ? new Date(o.startDate).toLocaleDateString() : null, o.stage].filter(Boolean).join(' · ')}</Text>
                      </View>
                      <FontAwesome5 name="chevron-right" size={10} color="#c2ccd6" />
                    </TouchableOpacity>
                  );
                })}

              <Text style={styles.infoTitle}>Reports &amp; requests</Text>
              {contextLoading ? null
                : context.reports.length === 0 ? <Text style={styles.infoEmpty}>No reports.</Text>
                : context.reports.slice(0, 12).map((r: any) => {
                  const active = detail?.type === 'report' && detail.data === r;
                  return (
                    <TouchableOpacity key={r.id} style={[styles.infoRow, active && styles.infoRowActive]} onPress={() => setDetail({ type: 'report', data: r })}>
                      <FontAwesome5 name="exclamation-circle" size={11} color="#B26A00" />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.infoRowTitle}>{String(r.category || 'Report').replace(/_/g, ' ')}</Text>
                        <Text style={styles.infoRowSub} numberOfLines={1}>{[r.status, r.createdAt ? new Date(r.createdAt).toLocaleDateString() : null].filter(Boolean).join(' · ')}</Text>
                      </View>
                      <FontAwesome5 name="chevron-right" size={10} color="#c2ccd6" />
                    </TouchableOpacity>
                  );
                })}
            </ScrollView>

            {/* Detail pane — always present (dedicated bottom-right area) */}
            <View style={styles.detailPane}>
              <View style={styles.detailHead}>
                <Text style={styles.detailTitle}>
                  {!detail ? 'Details'
                    : detail.type === 'order' ? `Order #${detail.data.orderNumber}`
                    : `${String(detail.data.category || 'Report').replace(/_/g, ' ')} · Report #${detail.data.id}`}
                </Text>
                {!!detail && <TouchableOpacity onPress={() => setDetail(null)}><FontAwesome5 name="times" size={14} color="#5B6672" /></TouchableOpacity>}
              </View>
              {!detail ? (
                <View style={styles.detailPlaceholder}>
                  <FontAwesome5 name="hand-pointer" size={20} color="#c2ccd6" />
                  <Text style={styles.detailPlaceholderText}>Tap an order or report — here or in the chat — to see its details.</Text>
                </View>
              ) : (
                <ScrollView contentContainerStyle={{ padding: 14, gap: 10 }}>
                  {detail.type === 'order' ? (
                    <>
                      <View style={styles.dRow}><Text style={styles.dLabel}>Store</Text><Text style={styles.dVal}>{detail.data.storeName || detail.data.tenantDisplayName || '—'}</Text></View>
                      <View style={styles.dRow}><Text style={styles.dLabel}>Status</Text><Text style={styles.dVal}>{detail.data.stage || '—'}</Text></View>
                      <View style={styles.dRow}><Text style={styles.dLabel}>Dates</Text><Text style={styles.dVal}>{[detail.data.startDate ? new Date(detail.data.startDate).toLocaleDateString() : null, detail.data.endDate ? new Date(detail.data.endDate).toLocaleDateString() : null].filter(Boolean).join(' → ') || '—'}</Text></View>
                      <View style={styles.dRow}><Text style={styles.dLabel}>Address</Text><Text style={styles.dVal}>{detail.data.address || '—'}</Text></View>
                    </>
                  ) : (
                    <>
                      <View style={styles.dRow}><Text style={styles.dLabel}>Status</Text><Text style={styles.dVal}>{String(detail.data.status || 'submitted').replace(/_/g, ' ')}</Text></View>
                      <View style={styles.dRow}><Text style={styles.dLabel}>Reported</Text><Text style={styles.dVal}>{detail.data.createdAt ? new Date(detail.data.createdAt).toLocaleString() : '—'}</Text></View>
                      {!!detail.data.customText && <View style={styles.dRow}><Text style={styles.dLabel}>Details</Text><Text style={styles.dVal}>{detail.data.customText}</Text></View>}
                      {!!detail.data.officeNote && <View style={styles.dNote}><Text style={styles.dNoteLabel}>Store note</Text><Text style={styles.dVal}>{detail.data.officeNote}</Text></View>}
                      {Array.isArray(detail.data.photos) && detail.data.photos.length > 0 && (
                        <View style={styles.photos}>
                          {detail.data.photos.map((p: any, i: number) => (
                            <Image key={i} source={{ uri: typeof p === 'string' ? p : p.url }} style={styles.photo} />
                          ))}
                        </View>
                      )}
                    </>
                  )}
                </ScrollView>
              )}
            </View>
          </View>
        )}
      </View>
    </BasicLayout>
  );
};

const styles = StyleSheet.create({
  wrap: { flex: 1, flexDirection: 'row', padding: 16, gap: 16 },
  left: { width: 360, backgroundColor: '#fff', borderWidth: 1, borderColor: '#e2e2e2', borderRadius: 10, overflow: 'hidden' },
  leftHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 14, paddingTop: 12 },
  title: { fontSize: 18, fontWeight: 'bold', color: '#172F48' },
  search: { margin: 12, marginTop: 10, backgroundColor: '#f7f9fb', paddingVertical: 8, paddingHorizontal: 12, borderWidth: 1, borderColor: '#dde3e9', borderRadius: 8, fontSize: 14 },
  threadsList: { flex: 1 },
  empty: { padding: 20, color: '#888' },
  convRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 11, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  convRowActive: { backgroundColor: '#EAF4FC' },
  avatar: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#2e96e1', alignItems: 'center', justifyContent: 'center' },
  convTop: { flexDirection: 'row', justifyContent: 'space-between' },
  convName: { fontWeight: 'bold', color: '#172F48', flex: 1 },
  convTime: { color: '#999', fontSize: 11, marginLeft: 8 },
  convSub: { color: '#2e96e1', fontSize: 11, marginTop: 1 },
  convPreview: { color: '#666', fontSize: 13, marginTop: 2 },
  badge: { minWidth: 20, height: 20, borderRadius: 10, backgroundColor: '#C8242C', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 5 },
  badgeText: { color: '#fff', fontSize: 11, fontWeight: 'bold' },
  pagination: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 8, borderTopWidth: 1, borderTopColor: '#eee' },
  pageInfo: { color: '#555', fontSize: 12 },
  pageBtn: { backgroundColor: '#2e96e1', borderRadius: 4, paddingVertical: 5, paddingHorizontal: 10 },
  pageBtnDisabled: { backgroundColor: '#b7c9d6' },
  pageBtnText: { color: '#fff', fontSize: 12 },
  right: { flex: 1, backgroundColor: '#fff', borderWidth: 1, borderColor: '#e2e2e2', borderRadius: 10, overflow: 'hidden' },
  noSel: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10 },
  noSelText: { color: '#8a97a4' },
  threadHeader: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderBottomWidth: 1, borderBottomColor: '#eee' },
  threadName: { fontSize: 16, fontWeight: 'bold', color: '#172F48' },
  threadSub: { color: '#888', fontSize: 12, marginTop: 2 },
  orderChip: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#EAF4FC', borderRadius: 14, paddingVertical: 5, paddingHorizontal: 10 },
  orderChipText: { color: '#1F6FB2', fontSize: 12, fontWeight: 'bold' },
  threadScroll: { flex: 1, backgroundColor: '#fafafa' },
  msgRow: { flexDirection: 'row' },
  msgLeft: { justifyContent: 'flex-start' },
  msgRight: { justifyContent: 'flex-end' },
  msg: { maxWidth: '72%', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 8 },
  msgCustomer: { backgroundColor: '#fff', borderWidth: 1, borderColor: '#e2e2e2' },
  msgAdmin: { backgroundColor: '#2e96e1' },
  msgOrder: { fontSize: 10, fontWeight: 'bold', color: '#1F6FB2', marginBottom: 3 },
  msgBody: { color: '#172F48', fontSize: 14 },
  msgTime: { fontSize: 10, marginTop: 4, alignSelf: 'flex-end' },
  composer: { flexDirection: 'row', alignItems: 'flex-end', gap: 10, padding: 12, borderTopWidth: 1, borderTopColor: '#eee' },
  composerInput: { flex: 1, minHeight: 44, maxHeight: 120, borderWidth: 1, borderColor: '#dde3e9', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, backgroundColor: '#fff' },
  sendBtn: { backgroundColor: '#2e96e1', borderRadius: 8, paddingVertical: 12, paddingHorizontal: 20 },
  sendBtnText: { color: '#fff', fontWeight: 'bold' },
  info: { width: 360, backgroundColor: '#fff', borderWidth: 1, borderColor: '#e2e2e2', borderRadius: 10, overflow: 'hidden' },
  infoTitle: { fontSize: 11, fontWeight: 'bold', color: '#8a97a4', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 6 },
  infoCard: { backgroundColor: '#f7f9fb', borderRadius: 8, padding: 12 },
  infoName: { fontSize: 15, fontWeight: 'bold', color: '#172F48' },
  infoLine: { fontSize: 12, color: '#5B6672', marginTop: 2 },
  infoEmpty: { fontSize: 12, color: '#9aa5b1', paddingVertical: 2 },
  infoRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#f2f2f2' },
  infoRowActive: { backgroundColor: '#EAF4FC', borderRadius: 6, marginHorizontal: -4, paddingHorizontal: 4 },
  infoRowTitle: { fontSize: 13, fontWeight: '600', color: '#172F48', textTransform: 'capitalize' },
  infoRowSub: { fontSize: 11, color: '#8a97a4', marginTop: 1 },
  detailPane: { flex: 1, borderTopWidth: 2, borderTopColor: '#e2e2e2', backgroundColor: '#fafcff' },
  detailPlaceholder: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10, padding: 24 },
  detailPlaceholderText: { fontSize: 12, color: '#9aa5b1', textAlign: 'center' },
  detailHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#eef2f6' },
  detailTitle: { fontSize: 14, fontWeight: 'bold', color: '#172F48', textTransform: 'capitalize', flex: 1 },
  dRow: { gap: 2 },
  dLabel: { fontSize: 10, fontWeight: 'bold', color: '#8a97a4', textTransform: 'uppercase', letterSpacing: 0.4 },
  dVal: { fontSize: 13, color: '#172F48' },
  dNote: { backgroundColor: '#fff', borderWidth: 1, borderColor: '#e8edf2', borderRadius: 8, padding: 8, gap: 2 },
  dNoteLabel: { fontSize: 10, fontWeight: 'bold', color: '#B26A00', textTransform: 'uppercase', letterSpacing: 0.4 },
  photos: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  photo: { width: 72, height: 72, borderRadius: 8, backgroundColor: '#eee' },
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.35)', alignItems: 'center', justifyContent: 'center' },
  modalCard: { width: 420, maxWidth: '90%', backgroundColor: '#fff', borderRadius: 12, padding: 20, gap: 12 },
  modalHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  modalTitle: { fontSize: 17, fontWeight: 'bold', color: '#172F48' },
  modalRow: { flexDirection: 'row', gap: 12 },
  modalLabel: { width: 80, fontSize: 13, color: '#8a97a4' },
  modalVal: { flex: 1, fontSize: 13, color: '#172F48', textTransform: 'capitalize' },
});

export default Messages;
